import apiClient from "../api/client";
import {
  PageVersion,
  PageVersionCount,
  PagedResult,
  AvailablePageVersion,
  ValidatePublishOverlapResponse,
  ScheduledAssignment,
  PublishDefaults,
  PageVersionConflict,
  PageVersionApprovalDetails
} from "../types/pageversiontypes";

export async function fetchAvailablePageVersions(): Promise<AvailablePageVersion[]> {
  const response = await apiClient.get<AvailablePageVersion[]>(
    "/api/PageVersion/GetAll"
  );
  return response.data;
}

export async function createPageVersion(
  name: string,
  createdBy: string
): Promise<{ id: string }> {
  const response = await apiClient.post(
    "/api/PageVersion/Create",
    { name, createdBy }
  );
  return response.data;
}

export async function sendPageVersionForApproval(payload: {
  pageVersionGuid: string;
  createdBy: string;
  selectedStores: string[];
  startDate: string;
  endDate: string;
  override?: boolean;
  conflicts?: PageVersionConflict[];
}): Promise<void> {
  await apiClient.post("/api/PageVersion/SendForApproval", payload);
}

export async function editPageVersion(
  guid: string,
  name: string
): Promise<void> {
  await apiClient.post("/api/PageVersion/Edit", { guid, name });
}

export async function updatePageVersionStatusToDraft(payload: {
  guid: string;
  name: string;
  createdBy: string;
}): Promise<{ id: string }> {
  const response = await apiClient.post(
    "/api/PageVersion/UpdatePageVersionStatusToDraft",
    payload
  );
  return response.data;
}

export async function getPublishDefaults(
  pageVersionGuid: string
): Promise<PublishDefaults> {
  const response = await apiClient.get<PublishDefaults>(
    "/api/PageVersion/GetPublishDefaults",
    {
      params: { pageVersionGuid },
    }
  );
  return response.data;
}

export async function validatePublishOverlap(
  storeGuids: string[],
  startDate: string,
  endDate: string | null,
  pageVersionName: string,
  pageVersionGuid: string
): Promise<ValidatePublishOverlapResponse> {
  const response = await apiClient.post<ValidatePublishOverlapResponse>(
    "/api/PageVersion/ValidatePublishOverlap",
    {
      storeGuids,
      startDate,
      endDate,
      pageVersionName,
      pageVersionGuid,
    }
  );
  return response.data;
}

export async function getScheduledAssignments(
  previewStartDate: string,
  previewEndDate: string
): Promise<ScheduledAssignment[]> {
  const response = await apiClient.get<ScheduledAssignment[]>(
    "/api/PageVersion/GetScheduledAssignments",
    {
      params: { previewStartDate, previewEndDate },
    }
  );
  return response.data;
}

export async function getPageVersionCount(): Promise<PageVersionCount> {
  const response = await apiClient.get<PageVersionCount>(
    "/api/PageVersion/Count"
  );
  return response.data;
}

export async function fetchPagedPageVersions(
  pageNumber: number,
  pageSize: number,
  searchText?: string,
  sortBy?: string,
  sortOrder?: string,
  activeFilter?: string
): Promise<PagedResult<PageVersion>> {
  const response = await apiClient.get<PagedResult<PageVersion>>(
    "/api/PageVersion/GetPagedPageVersions",
    {
      params: {
        PageNumber: pageNumber,
        PageSize: pageSize,
        SearchText: searchText,
        SortBy: sortBy,
        SortOrder: sortOrder,
        ActiveFilter: activeFilter,
      },
    }
  );
  return response.data;
}

export async function deleteDraftPageVersion(
  guid: string
): Promise<void> {
  await apiClient.delete(
    "/api/PageVersion/DeleteDraft",
    {
      params: { guid },
    }
  );
}

export async function getPageVersionApprovalDetails(
  approvalGuid: string
): Promise<PageVersionApprovalDetails> {
  const response =
    await apiClient.get<PageVersionApprovalDetails>(
      `/api/PageVersion/GetPageVersionApprovalDetails/${approvalGuid}`
    );

  return response.data;
}

export async function togglePageVersionActive(
  guid: string
): Promise<void> {
  await apiClient.post(
    `/api/PageVersion/TogglePageVersionActive/${guid}`
  );
}

export const checkPageVersionNameExists = async (name: string) => {
  const res = await apiClient.get(
    "/api/PageVersion/CheckNameExists",
    {
      params: { name },
    }
  );
  return res.data;
};

export const duplicatePageVersion = async (payload: {
  sourcePageVersionGuid: string;
  name: string;
  createdBy: string;
}) => {
  const res = await apiClient.post(
    "/api/PageVersion/DuplicatePageVersion",
    payload
  );
  return res.data;
};


export const getStoresForPageVersion = async (
  pageVersionGuid: string
) => {
  const res = await apiClient.get(
    "/api/PageVersion/GetStoresForPageVersion",
    {
      params: { Id: pageVersionGuid }
    }
  );
  return res.data;
};

export const getPagedPageVersionApprovals = async (params: {
  PageNumber: number;
  PageSize: number;
  SearchText?: string;
  SortBy?: React.Key;
  SortOrder?: string;
}) => {
  const res = await apiClient.get(
    "/api/PageVersion/GetPagedApprovals",
    { params }
  );
  return res.data;
};

export const getPageVersionApprovalById = async (id: string) => {
  const res = await apiClient.get(
    "/api/PageVersion/GetApprovalById",
    {
      params: { id },
    }
  );
  return res.data;
};

export const reviewPageVersionApproval = async (payload: {
  pageVersionGuid: string;
  isApproved: boolean;
  reviewerGuid: string;
  comment: string;
  pageVersionName?: string;
  requestedBy?: string;
}) => {
  return apiClient.post(
    "/api/PageVersion/ReviewApproval",
    payload
  );
};


